import { ILogger } from '@remento/logger';

import { Debugger } from '../../debugger.js';
import { Payload, Router } from '../../router.js';

import { ClickEvent, ClickPayload } from './@types.js';

export const ACTION_PROP_PREFIX = `data-analytics-event-prop-`;

function extractClickPayload(logger: ILogger, $element: HTMLElement): Payload<ClickPayload> | null {
  const analyticsEvent = $element.attributes.getNamedItem('data-analytics-event')?.value ?? null;
  if (analyticsEvent == null || analyticsEvent.length == 0) {
    logger.error('CLICK_EVENT', () => ({ error: 'ELEMENT_MISSING_DATA' }));
    return null;
  }
  const analyticsEventProps: Record<string, string> = {};
  for (let i = 0; i < $element.attributes.length; i++) {
    const attribute = $element.attributes.item(i);
    const attributeName = attribute?.name;
    if (attributeName == null || !attributeName.startsWith(ACTION_PROP_PREFIX)) {
      continue;
    }
    const attributeValue = attribute?.value;
    if (attributeValue == null || attributeValue.length == 0) {
      logger.warn('CLICK_EVENT', () => ({
        error: 'INVALID_ATTRIBUTE_VALUE',
        attribute: { name: attributeName, value: attributeValue },
      }));
      continue;
    }
    analyticsEventProps[attributeName.substring(ACTION_PROP_PREFIX.length)] = attributeValue;
  }
  let link = $element.getAttribute('href');
  if (link != null) {
    try {
      if (link.startsWith('clkn') || link.startsWith('clkg')) {
        // UNBOUNCE
        link = link.substring(5);
        if (link.startsWith('https/')) {
          link = 'https://' + link.substring(6);
        } else if (link.startsWith('http/')) {
          link = 'http://' + link.substring(5);
        }
        const url = new URL(link);
        analyticsEventProps.link = `${url.origin}${url.pathname}`;
      } else if (link.startsWith('//')) {
        // RELATIVE PATH
        link = window.location.origin + link.substring(1);
      } else if (link.startsWith('/')) {
        // RELATIVE PATH
        link = window.location.origin + link;
      } else if (link.startsWith('#')) {
        // ANCHOR
        link = window.location.origin + link;
      }
      const url = new URL(link);
      analyticsEventProps.link = `${url.origin}${url.pathname}`;
    } catch (err) {
      logger.warn('CLICK_EVENT', () => ({
        error: err,
        attribute: { name: 'href', value: link },
      }));
    }
  }
  return {
    data: { event: analyticsEvent },
    properties: analyticsEventProps,
  };
}

export function registerClickElementObserver(router: Router, logger: ILogger, $element: HTMLElement) {
  if ($element.dataset.actionRegistered != null) {
    logger.warn('CLICK_REGISTER_ERROR', () => ({ error: 'ALREADY_REGISTERED', $element }));
    return;
  }
  $element.dataset.actionRegistered = 'yes';
  $element.addEventListener('click', () => {
    const payload = extractClickPayload(logger, $element);
    if (payload == null) {
      return;
    }
    logger.debug(`CLICK`);
    router.publish(ClickEvent, payload);
  });
}

export function registerClickObserver(router: Router, debug: Debugger | null) {
  const logger = router.logger.extend('click-observer');
  const $elements = document.querySelectorAll('[data-analytics-event]');
  for (let i = 0; i < $elements.length; i++) {
    registerClickElementObserver(router, logger, $elements.item(i) as HTMLElement);
  }
  debug?.registerTooltips('click-observer', () => ({
    elements: Array.from($elements as NodeListOf<HTMLElement>),
    getEvent: ($element) => {
      const payload = extractClickPayload(logger, $element);
      return { type: payload?.data.event ?? null, payload };
    },
  }));
}
